import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useRef, useState, useEffect } from 'react';
import { EmailCaptureConfig, FormField } from '@/types/vsleads/emailCaptureTypes';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { VideoElement } from '@/components/video-player/VideoElement';
import { VideoFeatures } from '@/store/videoFeatures';

// Mesma ordem em que o player (e-player-js) monta os campos: nome → WhatsApp → e-mail
const PLAYER_FIELD_ORDER: FormField['type'][] = ['name', 'phone', 'email'];

interface EmailCapturePreviewProps {
  config: EmailCaptureConfig;
  videoUrl?: string | null;
  isLoading?: boolean;
  isCapturePassword?: boolean;
}

export function EmailCapturePreview({ config, videoUrl, isLoading = false, isCapturePassword = false }: EmailCapturePreviewProps) {
  useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);

  // Se o timer estiver desabilitado E recurso ativo, mostrar o formulário imediatamente
  const [showForm, setShowForm] = useState(config.isActive && !config.timer.enabled);

  // Parse timer moment to seconds
  const getTimerSeconds = (timeString: string): number => {
    const [minutes, seconds] = timeString.split(':').map(Number);
    return minutes * 60 + seconds;
  };

  const timerSeconds = config.timer.enabled ? getTimerSeconds(config.timer.moment) : 0;

  // Atualizar showForm quando o timer for habilitado/desabilitado OU quando isActive mudar
  useEffect(() => {
    if (!config.isActive) {
      // Se o recurso está desativado, NUNCA mostrar formulário
      setShowForm(false);
    } else if (!config.timer.enabled && !hasSubmitted) {
      // Se recurso ativo e timer desabilitado, mostrar formulário no início
      setShowForm(true);
    } else if (config.timer.enabled) {
      // Se timer habilitado, esconder formulário (vai aparecer no tempo certo)
      setShowForm(false);
    }
  }, [config.timer.enabled, config.isActive, hasSubmitted]);

  // Video event handlers
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;

    const current = video.currentTime;
    setCurrentTime(current);

    // Show form when reaching timer moment (SOMENTE se recurso estiver ativo)
    if (config.isActive && config.timer.enabled && current >= timerSeconds && !hasSubmitted) {
      setShowForm(true);
      if (config.timer.pauseOnOpen) {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleDurationChange = () => {
    const video = videoRef.current;
    if (video) {
      setDuration(video.duration);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleVolumeChange = () => {
    const video = videoRef.current;
    if (video) {
      setIsMuted(video.muted);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    // Se o formulário está sendo exibido e ainda não foi submetido (timer desabilitado), não permitir play
    if (showForm && !hasSubmitted && !config.timer.enabled) {
      return;
    }

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);
    setShowForm(false);

    // Se o timer está desabilitado (formulário no início), pausar após submit
    // Se o timer está habilitado, seguir a configuração pauseAfterSubmit
    const shouldPause = !config.timer.enabled || config.timer.pauseAfterSubmit;

    if (shouldPause && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleSkip = () => {
    setShowForm(false);
    setHasSubmitted(true);
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  // Formulário vertical (só captura de dados): campos empilhados e botão em
  // largura total — espelha o createForm do player (e-player-js).
  const isVerticalForm = !isCapturePassword && config.orientation === 'vertical';

  const titleFontSize = config.texts.headline && config.texts.headline.length > 50 ? '0.8rem' : '1.5rem';
  const footerFontSize = config.texts.footer && config.texts.footer.length > 50 ? '0.8rem' : '1rem';

  // Mesmos degraus de compactação do player: em players baixos (ex.: vídeo
  // 16:9) os campos empilhados não caberiam na altura do vídeo.
  const verticalFormCss = `
    .vsl-capture-vertical { container-type: size; }
    @container (max-height: 380px) {
      .vsl-capture-vertical .vsl-capture-title { font-size: min(0.95rem, ${titleFontSize}) !important; margin-bottom: 0.35rem !important; }
      .vsl-capture-vertical .vsl-capture-stack { gap: 0.4rem !important; }
      .vsl-capture-vertical .vsl-capture-stack > * { height: 34px !important; padding-top: 0 !important; padding-bottom: 0 !important; }
      .vsl-capture-vertical .vsl-capture-footer { font-size: min(0.75rem, ${footerFontSize}) !important; margin-top: 0.75rem !important; }
    }
    @container (max-height: 270px) {
      .vsl-capture-vertical .vsl-capture-title { font-size: min(0.8rem, ${titleFontSize}) !important; line-height: 1.2 !important; margin-bottom: 0.25rem !important; }
      .vsl-capture-vertical .vsl-capture-stack { gap: 0.3rem !important; }
      .vsl-capture-vertical .vsl-capture-stack > * { height: 28px !important; font-size: 0.75rem !important; }
      .vsl-capture-vertical .vsl-capture-footer { font-size: min(0.65rem, ${footerFontSize}) !important; line-height: 1.35 !important; margin-top: 0.5rem !important; }
      .vsl-capture-vertical .vsl-capture-skip { bottom: 0.5rem !important; right: 0.5rem !important; padding: 0.25rem 0.6rem !important; font-size: 0.7rem !important; }
    }
    @container (max-height: 200px) {
      .vsl-capture-vertical .vsl-capture-title { font-size: min(0.75rem, ${titleFontSize}) !important; margin-bottom: 0.2rem !important; }
      .vsl-capture-vertical .vsl-capture-stack { gap: 0.25rem !important; }
      .vsl-capture-vertical .vsl-capture-stack > * { height: 26px !important; }
      .vsl-capture-vertical .vsl-capture-footer { font-size: min(0.6rem, ${footerFontSize}) !important; line-height: 1.3 !important; margin-top: 0.4rem !important; }
    }
  `;

  const fieldStyle = {
    backgroundColor: config.style.fields.background,
    border: 'none',
    borderRadius: `${config.style.fields.radius}px`,
    padding: '0.6rem 0.85rem',
    fontSize: '0.85rem',
    height: '42px',
    outline: 'none',
    width: '100%',
  } as React.CSSProperties;

  const buttonStyle = {
    backgroundColor: config.style.button.normal,
    color: config.style.button.text,
    borderRadius: `${config.style.button.radius}px`,
    padding: '0.55rem 1.2rem',
    fontSize: '0.85rem',
    height: '42px',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
  } as React.CSSProperties;

  const activeFields = config.fields
    .filter(field => field.active !== false)
    .sort((a, b) => PLAYER_FIELD_ORDER.indexOf(a.type) - PLAYER_FIELD_ORDER.indexOf(b.type));
  const fieldCount = activeFields.length;

  // Determinar layout dos campos baseado na quantidade
  const shouldUseTwoRowLayout = fieldCount === 3; // 2 em cima, 1 embaixo

  // Create VideoFeatures object for VideoElement
  const videoFeatures: VideoFeatures = {
    videoUrl: videoUrl,
    showControls: false, // We'll use custom controls via the preview
    disableControls: false,
    autoPlay: false,
    loop: false,
    bigPlayButton: true,
    customThumbnail: null,
    initialThumbnail: null,
    largePlayer: true,
  };

  // Mostrar placeholder se não houver vídeo selecionado
  if (!videoUrl) {
    return (
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-sm overflow-hidden">
        <div className="aspect-video bg-gray-50 flex items-center justify-center">
          <div className="text-center px-6">
            <p className="text-sm text-gray-500">{translateInterfaceText("Selecione um vídeo para visualizar o preview")}</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-theme-isolated>
      {/* PLAYER REALISTA */}
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.08)] overflow-hidden">
        <CardContent className="p-0">
          {/* Container do Player - se ajusta ao tamanho natural do vídeo */}
          <div className="relative bg-[#0A0A0A] overflow-hidden w-full">

            {/* Loading State */}
            {isLoading && (
              <div className="w-full flex items-center justify-center bg-gray-900" style={{ aspectRatio: '16/9' }}>
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p className="text-white text-sm">{translateInterfaceText("Carregando vídeo...")}</p>
                </div>
              </div>
            )}

            {/* Vídeo Real */}
            {!isLoading && (
              <div className="relative w-full" key={videoUrl || 'default'}>
                <VideoElement
                  videoRef={videoRef}
                  features={videoFeatures}
                  togglePlay={togglePlay}
                  useCustomControls={false}
                  isMuted={isMuted}
                  showAutoplayOverlay={false}
                  autoplayOverlayEnabled={false}
                  onTimeUpdate={handleTimeUpdate}
                  onDurationChange={handleDurationChange}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onVolumeChange={handleVolumeChange}
                  maxHeight="500px"
                />
              </div>
            )}

            {/* Overlay de escurecimento (quando form aparece e overlay está ativo) */}
            {showForm && config.style.overlay.darkness > 0 && (
              <div
                className="absolute inset-0 transition-all duration-500 pointer-events-none"
                style={{
                  backgroundColor: config.style.container.background.value,
                  backdropFilter: config.style.overlay.blur > 0 ? `blur(${config.style.overlay.blur}px)` : 'none',
                }}
              />
            )}

            {/* FORMULÁRIO OVERLAY - Estilo do projeto antigo */}
            {showForm && (
              <section
                className={cn(
                  "absolute flex flex-col items-center z-[666]",
                  isVerticalForm && 'vsl-capture-vertical',
                  config.position === 'left' && 'left-0',
                  config.position === 'right' && 'right-0',
                  config.position === 'center' && 'left-1/2 -translate-x-1/2'
                )}
                style={{
                  bottom: 0,
                  height: '100%',
                  width: '100%',
                  backgroundColor: 'transparent',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                }}
              >
                {isVerticalForm && <style>{verticalFormCss}</style>}

                {/* TitleCapture - Título do formulário */}
                <h6
                  className="vsl-capture-title text-center mb-2"
                  style={{
                    fontSize: titleFontSize,
                    color: config.style.text.title,
                    width: '60%',
                    fontWeight: 'normal',
                    marginBottom: '0.5rem',
                    lineHeight: '1.3',
                  }}
                >
                  {config.texts.headline || translateInterfaceText("Insira os dados para continuar assistindo")}
                </h6>

                {/* CaptureInput - Container dos campos */}
                <div
                  className="flex justify-between flex-col"
                  style={{
                    width: '70%',
                  }}
                >
                  {/* Form */}
                  <form
                    onSubmit={handleSubmit}
                    className="flex flex-col"
                  >
                    {/* Campos de input */}
                    {isCapturePassword ? (
                      // Layout para captura por senha (apenas 1 campo)
                      <>
                        <div className="mb-3">
                          <input
                            type="password"
                            placeholder={config.texts.footer || translateInterfaceText("Digite a senha")}
                            disabled
                            style={{
                              ...fieldStyle,
                              width: '100%',
                            }}
                          />
                        </div>

                        {/* Botão de submit */}
                        <button
                          type="submit"
                          className="hover:brightness-110"
                          style={{
                            ...buttonStyle,
                            width: '100%',
                          }}
                        >
                          {config.texts.buttonText || translateInterfaceText("Acessar")}
                        </button>
                      </>
                    ) : isVerticalForm ? (
                      // Layout vertical: todos os campos empilhados + botão em largura total
                      <div className="vsl-capture-stack flex flex-col" style={{ gap: '0.55rem' }}>
                        {activeFields.map((field) => (
                          <input
                            key={field.id}
                            type={field.type}
                            placeholder={field.placeholder}
                            required={field.required}
                            style={fieldStyle}
                          />
                        ))}
                        <button
                          type="submit"
                          className="hover:brightness-110"
                          style={{
                            ...buttonStyle,
                            width: '100%',
                          }}
                        >
                          {config.texts.buttonText || translateInterfaceText("Play")}
                        </button>
                      </div>
                    ) : shouldUseTwoRowLayout ? (
                      // Layout para 3 campos: 2 em cima, 1 embaixo com botão
                      <>
                        <div className="flex gap-2 mb-3">
                          {activeFields.slice(0, 2).map((field) => (
                            <input
                              key={field.id}
                              type={field.type}
                              placeholder={field.placeholder}
                              required={field.required}
                              style={{ ...fieldStyle, width: 'calc(50% - 0.25rem)' }}
                            />
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            key={activeFields[2].id}
                            type={activeFields[2].type}
                            placeholder={activeFields[2].placeholder}
                            required={activeFields[2].required}
                            style={{ ...fieldStyle, flex: '1' }}
                          />
                          <button
                            type="submit"
                            className="hover:brightness-110"
                            style={{
                              ...buttonStyle,
                              minWidth: '30%',
                            }}
                          >
                            {config.texts.buttonText || translateInterfaceText("Play")}
                          </button>
                        </div>
                      </>
                    ) : (
                      // Layout para 1 ou 2 campos
                      <>
                        <div className="flex gap-2 mb-3">
                          {activeFields.map((field) => (
                            <input
                              key={field.id}
                              type={field.type}
                              placeholder={field.placeholder}
                              required={field.required}
                              style={{
                                ...fieldStyle,
                                width: fieldCount === 2 ? 'calc(50% - 0.25rem)' : '100%'
                              }}
                            />
                          ))}
                        </div>

                        {/* Botão de submit */}
                        <button
                          type="submit"
                          className="hover:brightness-110"
                          style={{
                            ...buttonStyle,
                            width: '100%',
                          }}
                        >
                          {config.texts.buttonText || translateInterfaceText("Play")}
                        </button>
                      </>
                    )}
                  </form>
                </div>

                {/* TextCaptured - Texto auxiliar/footer (não mostrar quando for senha) */}
                {!isCapturePassword && config.texts.footer && (
                  <h6
                    className="vsl-capture-footer text-center mt-2.5"
                    style={{
                      fontSize: footerFontSize,
                      color: config.style.text.footer,
                      width: '60%',
                      lineHeight: '150%',
                      fontWeight: 'normal',
                      marginTop: isVerticalForm ? '1.25rem' : '10px',
                      marginBottom: 0,
                    }}
                  >
                    {config.texts.footer}
                  </h6>
                )}

                {/* SkipButton - Botão de pular */}
                {config.texts.skipEnabled && !config.timer.blockClose && (
                  <button
                    onClick={handleSkip}
                    className="vsl-capture-skip absolute bottom-4 right-4 flex items-center gap-2 hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: '#FFF',
                      fontSize: '0.8rem',
                      padding: '0.4rem 0.8rem',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      zIndex: 10,
                    }}
                  >
                    {translateInterfaceText("Ignorar")}<X className="w-3 h-3" />
                  </button>
                )}
              </section>
            )}

            {/* Play button overlay quando pausado */}
            {!isPlaying && !showForm && (
              <div
                className="absolute inset-0 flex items-center justify-center cursor-pointer"
                onClick={togglePlay}
              >
                <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border-2 border-white/20 shadow-2xl hover:scale-110 transition-transform">
                  <svg className="w-10 h-10 text-white ml-1.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function getShadow(level: number): string {
  switch (level) {
    case 0:
      return 'none';
    case 1:
      return '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)';
    case 2:
      return '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)';
    case 3:
      return '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)';
    default:
      return 'none';
  }
}
